import { useEffect, useRef, useState, memo } from "react";
import { useJsApiLoader, GoogleMap } from "@react-google-maps/api";
import type { RoutePoint, RouteStatus } from "../../../store/admin/vehicleRouteTrackingStore";
import { buildRouteSegments } from "../../../store/admin/vehicleRouteTrackingStore";
import { formatDateTime } from "../utils/reportPointProcessors";
import { STOP_MARKER_ICON_URL, STOP_MARKER_SIZE, buildStopPopupHtml } from "../../../utils/mapStopMarker";

const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry")[] = ["places"];
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 };
const DEFAULT_ZOOM = 5;

const HIT_STROKE_WEIGHT = 32;

const STATUS_LABELS: Record<RouteStatus, string> = {
  driving: "Manejando",
  ralenti: "Ralentí",
  stopped: "Detenido",
};

const STATUS_COLORS: Record<RouteStatus, string> = {
  driving: "#22C55E",
  ralenti: "#F59E0B",
  stopped: "#EF4444",
};

export interface SelectedRoutePoint {
  vehicleName: string;
  ts: number;
  lat: number;
  lng: number;
  fecha: string;
  velocidad: string;
  status: RouteStatus;
}

const popupHtml = (
  fecha: string,
  velocidad: string,
  status: RouteStatus,
  variant: "hover" | "pinned",
): string =>
  `<div class="report-map-popup report-map-popup--${variant}">
    <div class="report-map-popup__title">${fecha}</div>
    <div>Velocidad: ${velocidad}</div>
    <div class="report-map-popup__status">
      <span class="report-map-popup__dot" style="background:${STATUS_COLORS[status]}"></span>
      ${STATUS_LABELS[status]}
    </div>
  </div>`;

export interface MapRoute {
  deviceId: string;
  vehicleName: string;
  color: string;
  points: RoutePoint[];
}

interface RoutePolylineProps {
  map: google.maps.Map;
  points: RoutePoint[];
  color: string;
  colorByState: boolean;
  tz: string;
  vehicleName: string;
  onSelect: (point: SelectedRoutePoint) => void;
}

const buildSelected = (
  p: RoutePoint,
  vehicleName: string,
  tz: string,
): SelectedRoutePoint => ({
  vehicleName,
  ts: p.ts,
  lat: p.lat,
  lng: p.lng,
  fecha: formatDateTime(p.ts, tz),
  velocidad: p.speed != null ? `${p.speed.toFixed(1)} km/h` : "N/A",
  status: p.status,
});

const RoutePolyline = ({ map, points, color, colorByState, tz, vehicleName, onSelect }: RoutePolylineProps) => {
  const linesRef = useRef<google.maps.Polyline[]>([]);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const stopMarkersRef = useRef<google.maps.Marker[]>([]);
  const infoRef = useRef<google.maps.InfoWindow | null>(null);
  const stopInfoRef = useRef<google.maps.InfoWindow | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (!map || points.length === 0) return;

    const { segments, stateMarkers } = buildRouteSegments(points);

    // Inicializar Geocoder
    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }

    const info = new google.maps.InfoWindow({ disableAutoPan: true });
    infoRef.current = info;

    const stopMarkers = stateMarkers.filter((m) => m.status === "stopped");

    let lastInfoKey = "";
    let infoOpen = false;
    let stopModalOpen = false;
    let closeTimer = 0;

    const openInfo = (content: string, position: google.maps.LatLngLiteral) => {
      window.clearTimeout(closeTimer);
      info.setPosition(position);
      if (content !== lastInfoKey) {
        info.setContent(content);
        lastInfoKey = content;
      }
      if (infoOpen) return;
      info.open(map);
      infoOpen = true;
    };

    const closeInfo = () => {
      window.clearTimeout(closeTimer);
      lastInfoKey = "";
      if (!infoOpen) return;
      info.close();
      infoOpen = false;
    };

    const scheduleCloseInfo = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(closeInfo, 160);
    };

    const findNearest = (latLng: google.maps.LatLng): RoutePoint => {
      const lat = latLng.lat();
      const lng = latLng.lng();
      let best = points[0];
      let bestDist = Infinity;
      for (const p of points) {
        const dx = p.lng - lng;
        const dy = p.lat - lat;
        const d = dx * dx + dy * dy;
        if (d < bestDist) {
          bestDist = d;
          best = p;
        }
      }
      return best;
    };

    const listeners: google.maps.MapsEventListener[] = [];

    segments.forEach((segment) => {
      const lineColor = colorByState ? segment.color : color;
      const line = new google.maps.Polyline({
        path: segment.path,
        strokeColor: lineColor,
        strokeOpacity: 0.9,
        strokeWeight: 4,
        clickable: false,
        zIndex: 3,
        icons:
          segment.status === "driving"
            ? [
                {
                  icon: {
                    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 2.2,
                    strokeColor: "#ffffff",
                    strokeWeight: 1.2,
                    fillColor: lineColor,
                    fillOpacity: 1,
                  },
                  offset: "0",
                  repeat: "140px",
                },
              ]
            : undefined,
      });
      line.setMap(map);
      linesRef.current.push(line);
    });

    const hitLine = new google.maps.Polyline({
      path: segments.flatMap((s) => s.path),
      strokeColor: "#000000",
      strokeOpacity: 0,
      strokeWeight: HIT_STROKE_WEIGHT,
      clickable: true,
      zIndex: 4,
    });
    hitLine.setMap(map);
    linesRef.current.push(hitLine);

    listeners.push(
      hitLine.addListener("mousemove", (e: google.maps.MapMouseEvent) => {
        if (!e.latLng || stopModalOpen) return;
        const p = findNearest(e.latLng);
        const velocidad = p.speed != null ? `${p.speed.toFixed(1)} km/h` : "N/A";
        openInfo(popupHtml(formatDateTime(p.ts, tz), velocidad, p.status, "hover"), {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        });
      }),
      hitLine.addListener("mouseout", () => scheduleCloseInfo()),
      hitLine.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        closeInfo();
        onSelect(buildSelected(findNearest(e.latLng), vehicleName, tz));
      }),
    );

    const start = points[0];
    const end = points[points.length - 1];

    const startMarker = new google.maps.Marker({
      position: { lat: start.lat, lng: start.lng },
      map,
      zIndex: 5,
      title: "Inicio",
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: "#10B981",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
    });

    const endMarker = new google.maps.Marker({
      position: { lat: end.lat, lng: end.lng },
      map,
      zIndex: 5,
      title: "Fin",
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: "#EF4444",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
    });

    markersRef.current = [startMarker, endMarker];

    const extractLocationInfo = (results: google.maps.GeocoderResult[]) => {
      if (!results || results.length === 0) return { state: "—", city: "—", street: "—" };
      const result = results[0];
      let state = "—", city = "—", street = "—";
      result.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("administrative_area_level_1")) {
          state = component.long_name;
        } else if (types.includes("locality")) {
          city = component.long_name;
        } else if (types.includes("route")) {
          street = component.long_name;
        }
      });
      return { state, city, street };
    };

    stopMarkers.forEach(marker => {
      const googleMarker = new google.maps.Marker({
        position: { lat: marker.lat, lng: marker.lng },
        map,
        zIndex: 6,
        icon: {
          url: STOP_MARKER_ICON_URL,
          scaledSize: new google.maps.Size(STOP_MARKER_SIZE, STOP_MARKER_SIZE),
          anchor: new google.maps.Point(STOP_MARKER_SIZE / 2, STOP_MARKER_SIZE / 2),
        },
      });

      googleMarker.addListener('click', () => {
        closeInfo();
        stopModalOpen = true;

        const popupData = {
          fecha: formatDateTime(marker.ts, tz),
          durationMs: marker.durationMs,
          lat: marker.lat,
          lng: marker.lng,
        };

        stopInfoRef.current?.close();

        stopInfoRef.current = new google.maps.InfoWindow({
          content: buildStopPopupHtml({ ...popupData, location: null }),
          maxWidth: 300,
        });

        listeners.push(
          stopInfoRef.current.addListener("closeclick", () => {
            stopModalOpen = false;
          }),
        );

        stopInfoRef.current.open(map, googleMarker);

        if (!geocoderRef.current) return;

        geocoderRef.current.geocode(
          { location: { lat: marker.lat, lng: marker.lng } },
          (results, status) => {
            if (status !== google.maps.GeocoderStatus.OK || !results || !stopInfoRef.current) return;
            const { state, city, street } = extractLocationInfo(results);
            stopInfoRef.current.setContent(
              buildStopPopupHtml({ ...popupData, location: `${state}, ${city}, ${street}` }),
            );
          },
        );
      });

      stopMarkersRef.current.push(googleMarker);
    });

    return () => {
      window.clearTimeout(closeTimer);
      listeners.forEach((l) => google.maps.event.removeListener(l));
      infoRef.current?.close();
      infoRef.current = null;
      stopInfoRef.current?.close();
      stopInfoRef.current = null;
      linesRef.current.forEach((l) => l.setMap(null));
      linesRef.current = [];
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      stopMarkersRef.current.forEach((m) => m.setMap(null));
      stopMarkersRef.current = [];
    };
  }, [map, points, color, colorByState, tz, vehicleName, onSelect]);

  return null;
};

interface ReportRouteMapProps {
  routes: MapRoute[];
  height?: number;
  tz?: string;
  selected?: SelectedRoutePoint | null;
  onSelect?: (point: SelectedRoutePoint) => void;
  onClearSelected?: () => void;
  interactive?: boolean;
}

const noop = () => {};

const STATIC_MAP_OPTIONS: google.maps.MapOptions = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  zoomControl: false,
  rotateControl: false,
  scaleControl: false,
  keyboardShortcuts: false,
  clickableIcons: false,
  disableDoubleClickZoom: true,
  draggable: false,
  scrollwheel: false,
  gestureHandling: "none",
};

const INTERACTIVE_MAP_OPTIONS: google.maps.MapOptions = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  zoomControl: true,
  rotateControl: false,
  scaleControl: false,
  keyboardShortcuts: false,
  clickableIcons: false,
  draggable: true,
  gestureHandling: "greedy",
  scrollwheel: true,
};

const ReportRouteMap = memo(({
  routes,
  height = 420,
  tz,
  selected,
  onSelect = noop,
  onClearSelected = noop,
  interactive = true,
}: ReportRouteMapProps) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const resolvedTz = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const selectedMarkerRef = useRef<google.maps.Marker | null>(null);
  const selectedInfoRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!map) return;
    const allPoints = routes.flatMap((r) => r.points);
    if (allPoints.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    allPoints.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, 60);
  }, [map, routes]);

  useEffect(() => {
    if (!map) return;

    selectedMarkerRef.current?.setMap(null);
    selectedMarkerRef.current = null;
    selectedInfoRef.current?.close();
    selectedInfoRef.current = null;

    if (!selected) return;

    const pos = { lat: selected.lat, lng: selected.lng };
    map.panTo(pos);
    if ((map.getZoom() ?? DEFAULT_ZOOM) < 14) map.setZoom(15);

    const marker = new google.maps.Marker({
      position: pos,
      map,
      zIndex: 10,
      animation: google.maps.Animation.BOUNCE,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#2563EB",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
    });
    selectedMarkerRef.current = marker;

    const bounceTimer = window.setTimeout(() => marker.setAnimation(null), 2100);

    const info = new google.maps.InfoWindow({
      content: popupHtml(selected.fecha, selected.velocidad, selected.status, "pinned"),
      position: pos,
    });
    info.open(map);
    selectedInfoRef.current = info;

    const closeListener = info.addListener("closeclick", () => onClearSelected());

    return () => {
      window.clearTimeout(bounceTimer);
      google.maps.event.removeListener(closeListener);
      marker.setMap(null);
      info.close();
    };
  }, [map, selected, onClearSelected]);

  if (!isLoaded) {
    return (
      <div
        className="w-full flex items-center justify-center text-sm text-gray-500"
        style={{ height }}
      >
        Cargando mapa…
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: `${height}px`, borderRadius: "0.5rem" }}
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      onLoad={(m) => setMap(m)}
      onUnmount={() => setMap(null)}
      onClick={() => onClearSelected()}
      options={interactive ? INTERACTIVE_MAP_OPTIONS : STATIC_MAP_OPTIONS}
    >
      {map &&
        routes.map((r) =>
          r.points.length > 0 ? (
            <RoutePolyline
              key={r.deviceId}
              map={map}
              points={r.points}
              color={r.color}
              colorByState={routes.length === 1}
              tz={resolvedTz}
              vehicleName={r.vehicleName}
              onSelect={onSelect}
            />
          ) : null,
        )}
    </GoogleMap>
  );
});

export default ReportRouteMap;
